import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ProductTable = ({ 
  products, 
  selectedProducts, 
  onProductSelect, 
  onSelectAll, 
  onSort, 
  sortConfig, 
  onEdit, 
  onDuplicate, 
  onArchive,
  onInlineEdit 
}) => {
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');

  const handleSort = (field) => {
    const direction = sortConfig.field === field && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    onSort({ field, direction });
  };

  const getSortIcon = (field) => {
    if (sortConfig.field !== field) return 'ArrowUpDown';
    return sortConfig.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  const getStockStatusBadge = (stock, minStock = 10) => {
    if (stock === 0) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-body-medium bg-error-100 text-error">
          <Icon name="AlertCircle" size={12} className="mr-1" />
          Sin stock
        </span>
      );
    } else if (stock <= minStock) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-body-medium bg-warning-100 text-warning">
          <Icon name="AlertTriangle" size={12} className="mr-1" />
          Stock bajo
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-body-medium bg-success-100 text-success">
          <Icon name="CheckCircle" size={12} className="mr-1" />
          En stock
        </span>
      );
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const handleInlineEditStart = (productId, field, currentValue) => {
    setEditingCell(`${productId}-${field}`);
    setEditValue(currentValue.toString());
  };

  const handleInlineEditSave = (productId, field) => {
    const numericValue = field === 'price' ? parseFloat(editValue) : parseInt(editValue);
    if (!isNaN(numericValue)) {
      onInlineEdit(productId, field, numericValue);
    }
    setEditingCell(null);
    setEditValue('');
  };

  const handleInlineEditCancel = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const renderEditableCell = (product, field, value) => {
    const cellKey = `${product.id}-${field}`;
    const isEditing = editingCell === cellKey;

    if (isEditing) {
      return (
        <div className="flex items-center space-x-1">
          <Input
            type="number"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="w-20 text-sm"
            step={field === 'price' ? '0.01' : '1'}
            min="0"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleInlineEditSave(product.id, field);
              } else if (e.key === 'Escape') {
                handleInlineEditCancel();
              }
            }}
          />
          <button
            onClick={() => handleInlineEditSave(product.id, field)}
            className="text-success hover:text-success-600"
          >
            <Icon name="Check" size={14} />
          </button>
          <button
            onClick={handleInlineEditCancel}
            className="text-error hover:text-error-600"
          >
            <Icon name="X" size={14} />
          </button>
        </div>
      );
    }

    return (
      <button
        onClick={() => handleInlineEditStart(product.id, field, value)}
        className="flex items-center space-x-1 hover:bg-secondary-50 px-2 py-1 rounded group"
      >
        <span>{field === 'price' ? formatPrice(value) : value}</span>
        <Icon 
          name="Edit2" 
          size={12} 
          className="text-text-muted opacity-0 group-hover:opacity-100 transition-micro"
        />
      </button>
    );
  };

  return (
    <div className="bg-surface rounded-lg border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-secondary-50 border-b border-border">
            <tr>
              <th className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  checked={selectedProducts.length === products.length && products.length > 0}
                  onChange={onSelectAll}
                  className="w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-body-medium text-text-secondary uppercase tracking-wider">
                Producto
              </th>
              <th className="px-4 py-3 text-left text-xs font-body-medium text-text-secondary uppercase tracking-wider">
                <button
                  onClick={() => handleSort('sku')}
                  className="flex items-center space-x-1 hover:text-text-primary"
                >
                  <span>SKU</span>
                  <Icon name={getSortIcon('sku')} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-body-medium text-text-secondary uppercase tracking-wider">
                <button
                  onClick={() => handleSort('price')}
                  className="flex items-center space-x-1 hover:text-text-primary"
                >
                  <span>Precio</span>
                  <Icon name={getSortIcon('price')} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-body-medium text-text-secondary uppercase tracking-wider">
                <button
                  onClick={() => handleSort('stock')}
                  className="flex items-center space-x-1 hover:text-text-primary"
                >
                  <span>Stock</span>
                  <Icon name={getSortIcon('stock')} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-body-medium text-text-secondary uppercase tracking-wider">
                Estado
              </th>
              <th className="px-4 py-3 text-left text-xs font-body-medium text-text-secondary uppercase tracking-wider">
                <button
                  onClick={() => handleSort('lastUpdated')}
                  className="flex items-center space-x-1 hover:text-text-primary"
                >
                  <span>Actualizado</span>
                  <Icon name={getSortIcon('lastUpdated')} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-right text-xs font-body-medium text-text-secondary uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {products.map((product) => (
              <tr 
                key={product.id} 
                className={`hover:bg-secondary-50 transition-micro ${
                  selectedProducts.includes(product.id) ? 'bg-primary-50' : ''
                }`}
              >
                <td className="px-4 py-4">
                  <input
                    type="checkbox"
                    checked={selectedProducts.includes(product.id)}
                    onChange={() => onProductSelect(product.id)}
                    className="w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
                  />
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-secondary-100">
                      <Image
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <div className="text-sm font-body-medium text-text-primary">
                        {product.name}
                      </div>
                      <div className="text-xs text-text-muted">
                        {product.category}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className="text-sm font-data text-text-primary">
                    {product.sku}
                  </span>
                </td>
                <td className="px-4 py-4">
                  {renderEditableCell(product, 'price', product.price)}
                </td>
                <td className="px-4 py-4">
                  {renderEditableCell(product, 'stock', product.stock)}
                </td>
                <td className="px-4 py-4">
                  {getStockStatusBadge(product.stock, product.minStock)}
                </td>
                <td className="px-4 py-4">
                  <span className="text-sm text-text-muted">
                    {new Date(product.lastUpdated).toLocaleDateString('es-ES')}
                  </span>
                </td>
                <td className="px-4 py-4 text-right">
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(product.id)}
                      iconName="Edit2"
                      iconSize={16}
                    >
                      Editar
                    </Button>
                    <div className="relative group">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="MoreVertical"
                        iconSize={16}
                      />
                      <div className="absolute right-0 top-full mt-1 w-48 bg-surface rounded-lg shadow-modal border border-border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-micro z-dropdown">
                        <div className="py-2">
                          <button
                            onClick={() => onDuplicate(product.id)}
                            className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-text-secondary hover:bg-secondary-50 hover:text-text-primary"
                          >
                            <Icon name="Copy" size={16} />
                            <span>Duplicar</span>
                          </button>
                          <button
                            onClick={() => onArchive(product.id)}
                            className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-error hover:bg-error-50"
                          >
                            <Icon name="Archive" size={16} />
                            <span>Archivar</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductTable;